import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { Button, TextInput } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import Accordion from '@components/ui/Accordion/Accordion';
import Modal from '@components/ui/Modal/Modal';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import {
  getCommercialTerms,
  replaceCommercialTerms,
} from '@/services/commercialTermService';
import { getProductTypes, resolveCountries } from '@/services/tenantService';
import { useCountries } from '@/hooks/useCountries';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/hooks/useTranslation';
import type { CommercialTermDto } from '@/types/commercialTerm';
import type { ProductTypeCountryResolution } from '@/types/tenant';
import styles from './CommercialTermsTab.module.scss';

// ============================================
// TYPES
// ============================================

interface CommercialTermsTabProps {
  tenantId: string;
  /** Tenant's assigned product type IDs — only these are selectable in edit modal */
  tenantProductTypeIds: string[];
}

interface EditRow {
  _id: string;
  /** Persisted row id when the row was hydrated from the server. Empty for newly added rows. */
  serverId?: string;
  productTypeId: string;
  countryId: string;
  employmentTypeId: string;
  employeeCount: string;
}

let editRowCounter = 0;
function nextEditRowId(): string {
  editRowCounter += 1;
  return `ct_edit_${editRowCounter}`;
}

// ============================================
// ICONS
// ============================================

function EditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.25 3H3C2.60218 3 2.22064 3.15804 1.93934 3.43934C1.65804 3.72064 1.5 4.10218 1.5 4.5V15C1.5 15.3978 1.65804 15.7794 1.93934 16.0607C2.22064 16.342 2.60218 16.5 3 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V9.75M13.875 1.875C14.1734 1.57663 14.578 1.40901 15 1.40901C15.422 1.40901 15.8266 1.57663 16.125 1.875C16.4234 2.17337 16.591 2.57805 16.591 3C16.591 3.42195 16.4234 3.82663 16.125 4.125L9 11.25L6 12L6.75 9L13.875 1.875Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

function CommercialTermsTab({ tenantId, tenantProductTypeIds }: CommercialTermsTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editRows, setEditRows] = useState<EditRow[]>([]);
  const [saving, setSaving] = useState(false);

  // Fetch existing commercial terms (BE accepts TENANTS_VIEW / ENTITIES_VIEW / TENANT_PROFILE_VIEW)
  const { data: termsData, isLoading: termsLoading, error: termsError } = useQuery({
    queryKey: ['commercialTerms', 'TENANT', tenantId],
    queryFn: () => getCommercialTerms('TENANT', tenantId, { size: 100 }),
  });

  // Resolve available countries + employment types per product type
  const { data: countryResolutions } = useQuery<ProductTypeCountryResolution[]>({
    queryKey: ['resolveCountries', tenantProductTypeIds],
    queryFn: () => resolveCountries(tenantProductTypeIds),
    enabled: tenantProductTypeIds.length > 0,
  });

  // Reference data (for label lookup)
  const { countries } = useCountries();

  // Reference data — only consumed by the edit modal options. Fires on every tab mount; if
  // the user lacks the perm the BE returns 403 and `data` stays undefined (modal options just
  // render empty — modal itself is gated by TENANTS_UPDATE so non-editors never see it).
  const { data: productTypesData } = useQuery({
    queryKey: ['productTypes'],
    queryFn: () => getProductTypes({ size: 100 }),
  });

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes'],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });

  const terms = termsData?.content ?? [];
  const employmentTypes = employmentTypesData?.content ?? [];

  const countryOptions = useMemo(
    () => countries.map((c) => ({ label: c.name, value: c.id })),
    [countries],
  );

  const allProductTypes = productTypesData?.content ?? [];

  // Product type options: only tenant's assigned product types, labels from fetched data
  const productTypeOptions = useMemo(() => {
    const ptMap = new Map(allProductTypes.map((pt) => [pt.id, pt.productType]));
    return tenantProductTypeIds.map((id) => ({
      label: ptMap.get(id) ?? id,
      value: id,
    }));
  }, [tenantProductTypeIds, allProductTypes]);

  const employmentTypeOptions = useMemo(
    () => employmentTypes.map((et) => ({ label: et.name, value: et.id })),
    [employmentTypes],
  );

  // Group terms by product type for display
  const termsByProductType = useMemo(() => {
    const map = new Map<string, CommercialTermDto[]>();
    for (const term of terms) {
      const key = term.productTypeId;
      const existing = map.get(key) ?? [];
      existing.push(term);
      map.set(key, existing);
    }
    return map;
  }, [terms]);

  // --- Edit handlers ---
  const handleOpenEdit = useCallback(() => {
    // Populate edit rows from existing terms — keep the persisted id around so the
    // diff-aware replace endpoint can match unchanged rows and skip emitting audits
    // for them.
    const rows: EditRow[] = terms.map((t) => ({
      _id: nextEditRowId(),
      serverId: t.id,
      productTypeId: t.productTypeId,
      countryId: t.countryId,
      employmentTypeId: t.employmentTypeId,
      employeeCount: String(t.employeeCount),
    }));
    setEditRows(rows);
    setIsEditModalOpen(true);
  }, [terms]);

  const handleCloseEdit = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const addEditRow = useCallback(() => {
    setEditRows((prev) => [
      ...prev,
      { _id: nextEditRowId(), productTypeId: '', countryId: '', employmentTypeId: '', employeeCount: '' },
    ]);
  }, []);

  const removeEditRow = useCallback((id: string) => {
    setEditRows((prev) => prev.filter((r) => r._id !== id));
  }, []);

  const updateEditRow = useCallback((id: string, patch: Partial<EditRow>) => {
    setEditRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, ...patch } : r)),
    );
  }, []);

  const handleSave = useCallback(async () => {
    // Validate: all fields required
    for (const row of editRows) {
      if (!row.productTypeId || !row.countryId || !row.employmentTypeId || !row.employeeCount) {
        addNotification({ variant: 'critical', title: t('K_VALIDATION_COMMERCIAL_FIELDS_REQUIRED', 'All commercial term fields are required.') });
        return;
      }
      const count = Number(row.employeeCount);
      if (!Number.isInteger(count) || count <= 0) {
        addNotification({ variant: 'critical', title: t('K_VALIDATION_EMPLOYEE_COUNT_POSITIVE', 'Employee count must be a positive whole number.') });
        return;
      }
    }
    // Check no duplicate productType + country + employmentType combo
    const combos = new Set<string>();
    for (const row of editRows) {
      const key = `${row.productTypeId}::${row.countryId}::${row.employmentTypeId}`;
      if (combos.has(key)) {
        addNotification({ variant: 'critical', title: t('K_VALIDATION_DUPLICATE_COMMERCIAL_TERM', 'Duplicate product type, country, and employment type combination found.') });
        return;
      }
      combos.add(key);
    }
    // Check all tenant product types are covered
    const coveredProductTypes = new Set(editRows.map((r) => r.productTypeId));
    for (const ptId of tenantProductTypeIds) {
      if (!coveredProductTypes.has(ptId)) {
        addNotification({ variant: 'critical', title: t('K_VALIDATION_CT_ALL_PT_REQUIRED', 'All product types must have at least one commercial term.') });
        return;
      }
    }

    setSaving(true);
    try {
      // Diff-aware replace — server reconciles against persisted state and only emits
      // audits for rows that actually changed (UPDATED / CREATED / DELETED). Replaces
      // the legacy delete-all + bulk-create flow that produced one DELETE + one CREATE
      // audit per row even when nothing changed.
      await replaceCommercialTerms(
        'TENANT',
        tenantId,
        editRows.map((r) => ({
          ...(r.serverId ? { id: r.serverId } : {}),
          countryId: r.countryId,
          productTypeId: r.productTypeId,
          employmentTypeId: r.employmentTypeId,
          employeeCount: Number(r.employeeCount),
        })),
      );
      void queryClient.invalidateQueries({ queryKey: ['commercialTerms', 'TENANT', tenantId] });
      setIsEditModalOpen(false);
    } catch (error) {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    } finally {
      setSaving(false);
    }
  }, [editRows, tenantId, tenantProductTypeIds, queryClient, addNotification]);

  // --- Loading ---
  if (termsLoading) {
    return <div className={styles.loading}>{t('K_LOADING_COMMERCIAL_TERMS', 'Loading commercial terms...')}</div>;
  }

  // --- Forbidden (inline — rest of profile + sibling tabs stay interactive) ---
  if (isForbiddenError(termsError)) {
    return <Forbidden variant="inline" hideAction />;
  }

  return (
    <div className={styles.container}>
      <Accordion
        title={t('K_COMMERCIAL_TERMS', 'Commercial Terms')}
        variant="filled"
        whiteBody
        defaultExpanded
        action={
          hasPermission('TENANTS_UPDATE') ? (
            <button
              type="button"
              className={styles.editButton}
              onClick={handleOpenEdit}
              aria-label="Edit commercial terms"
            >
              <EditIcon />
            </button>
          ) : undefined
        }
      >
        {terms.length === 0 ? (
          <div className={styles.emptyState}>{t('K_NO_COMMERCIAL_TERMS', 'No commercial terms configured.')}</div>
        ) : (
          <div className={styles.termsList}>
            {Array.from(termsByProductType.entries()).map(([ptId, ptTerms]) => (
              <div key={ptId} className={styles.productTypeGroup}>
                <h4 className={styles.productTypeTitle}>
                  {ptTerms[0]?.productType?.productType ?? ptId}
                </h4>
                {ptTerms.map((term) => (
                  <div key={term.id} className={styles.termRow}>
                    <div className={styles.termField}>
                      <span className={styles.termLabel}>{t('K_LABEL_COUNTRY', 'Country')}</span>
                      <span className={styles.termValue}>{term.country?.name ?? term.countryId}</span>
                    </div>
                    <div className={styles.termField}>
                      <span className={styles.termLabel}>{t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}</span>
                      <span className={styles.termValue}>
                        {term.employmentType?.name ?? '-'}
                      </span>
                    </div>
                    <div className={styles.termField}>
                      <span className={styles.termLabel}>{t('K_LABEL_EMPLOYEE_COUNT', 'Employee Count')}</span>
                      <span className={styles.termValue}>{term.employeeCount ?? '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Accordion>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEdit}
        title={t('K_EDIT_COMMERCIAL_TERMS', 'Edit Commercial Terms')}
        size="xl"
        footer={
          <div className={styles.modalFooter}>
            <Button variant="outline" size="sm" onClick={handleCloseEdit} disabled={saving}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
            </Button>
          </div>
        }
      >
        <div className={styles.editContent}>
          {editRows.map((row) => {
            // Find resolution for this row's product type
            const resolution = countryResolutions?.find(
              (r) => r.productTypeId === row.productTypeId,
            );

            // Countries: ALL → all countries from store; LEGAL_ENTITY → resolved list
            const baseCountryOptions =
              resolution?.resolutionType === 'ALL'
                ? countryOptions
                : resolution?.countries
                  ? countryOptions.filter((c) =>
                      resolution.countries!.some((rc) => rc.countryId === c.value),
                    )
                  : [];

            // Employment types: ALL → all; LEGAL_ENTITY → per-country from resolution
            const baseRowEmploymentTypeOptions =
              !row.productTypeId || !row.countryId
                ? []
                : resolution?.resolutionType === 'ALL'
                  ? employmentTypeOptions
                  : (() => {
                      const countryEntry = resolution?.countries?.find(
                        (c) => c.countryId === row.countryId,
                      );
                      if (!countryEntry) return [];
                      return employmentTypeOptions.filter((et) =>
                        countryEntry.employmentTypeIds.includes(et.value),
                      );
                    })();

            // Hide employment types already chosen in sibling rows with the same
            // product type + country combo (keep current row's selection visible).
            // Mirrors CreateTenantModal's filter so duplicates cannot be constructed
            // in the UI. The save-time validator at line ~209 stays as belt-and-braces.
            const takenEmploymentTypeIds = new Set(
              editRows
                .filter(
                  (r) =>
                    r._id !== row._id &&
                    r.productTypeId === row.productTypeId &&
                    r.countryId === row.countryId &&
                    r.employmentTypeId,
                )
                .map((r) => r.employmentTypeId),
            );
            const rowEmploymentTypeOptions = baseRowEmploymentTypeOptions.filter(
              (et) => !takenEmploymentTypeIds.has(et.value),
            );

            return (
              <div key={row._id} className={styles.editRow}>
                <Select
                  label={t('K_LABEL_PRODUCT_TYPE', 'Product Type')}
                  isRequired
                  placeholder={t('K_PLACEHOLDER_SELECT', 'Select')}
                  options={productTypeOptions}
                  value={row.productTypeId}
                  onChange={(v: string) => updateEditRow(row._id, { productTypeId: v, countryId: '', employmentTypeId: '', employeeCount: '' })}
                  fullWidth
                />
                <Select
                  label={t('K_LABEL_COUNTRY', 'Country')}
                  isRequired
                  placeholder={t('K_PLACEHOLDER_SELECT', 'Select')}
                  options={baseCountryOptions}
                  value={row.countryId}
                  onChange={(v: string) => updateEditRow(row._id, { countryId: v, employmentTypeId: '', employeeCount: '' })}
                  searchable
                  fullWidth
                />
                <Select
                  label={t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}
                  isRequired
                  placeholder={
                    !row.countryId
                      ? t('K_PLACEHOLDER_SELECT_COUNTRY_FIRST', 'Select a country first')
                      : t('K_PLACEHOLDER_SELECT', 'Select')
                  }
                  options={rowEmploymentTypeOptions}
                  value={row.employmentTypeId}
                  onChange={(v: string) => updateEditRow(row._id, { employmentTypeId: v })}
                  disabled={!row.countryId}
                  fullWidth
                />
                <TextInput
                  label={t('K_LABEL_EMPLOYEE_COUNT', 'Employee Count')}
                  isRequired
                  type="number"
                  placeholder={t('K_PLACEHOLDER_EMPLOYEE_COUNT', 'Enter count')}
                  value={row.employeeCount}
                  onChange={(e) => updateEditRow(row._id, { employeeCount: e.target.value })}
                  fullWidth
                />
                <div className={styles.editRowActions}>
                  <Button variant="danger" size="sm" onClick={() => removeEditRow(row._id)}>
                    −
                  </Button>
                </div>
              </div>
            );
          })}
          <div className={styles.editAddRow}>
            <Button variant="outline" size="sm" onClick={addEditRow}>
              {t('K_BUTTON_ADD', '+ Add')}
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

export default CommercialTermsTab;
